import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Logo from "./Logo";
import PasswordForm from "./PasswordForm";
import { saveSession, sessionFromAccount } from "../data/session";

const base = process.env.REACT_APP_BACKEND_URL || "";

const EmailConfirmation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [busy, setBusy] = useState(false);
  const [passwordStep, setPasswordStep] = useState(null);

  const openAccount = (data) => {
    if (data.needsPassword) {
      setPending(null);
      setPasswordStep(data);
      return;
    }
    saveSession(sessionFromAccount(data, data.token));
    navigate("/", { replace: true });
  };

  const submitPassword = async (password) => {
    try {
      const response = await fetch(
        `${base}/api/confirmations/${encodeURIComponent(token || "")}/password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) return data.detail || "Enregistrement impossible.";
      saveSession(sessionFromAccount(data, data.token));
      if (passwordStep?.devLinks?.length) {
        try {
          sessionStorage.setItem("sp.devLinks.v1", JSON.stringify(passwordStep.devLinks));
        } catch {
          /* local links are only a convenience */
        }
      }
      navigate("/", { replace: true });
      return "";
    } catch {
      return "Le serveur ne répond pas.";
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetch(`${base}/api/confirmations/${encodeURIComponent(token || "")}`)
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (!response.ok) {
          setMessage(data.detail || "Ce lien de confirmation n'est plus valide.");
          return;
        }
        if (data.needsCode) {
          setPending(data);
          return;
        }
        openAccount(data);
      })
      .catch(() => {
        if (!cancelled) setMessage("Le serveur de confirmation ne répond pas.");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submitCode = async (event) => {
    event.preventDefault();
    setBusy(true);
    setCodeError("");
    try {
      const response = await fetch(
        `${base}/api/confirmations/${encodeURIComponent(token || "")}/activate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setCodeError(data.detail || "Code incorrect.");
        return;
      }
      openAccount(data);
    } catch {
      setCodeError("Le serveur ne répond pas.");
    } finally {
      setBusy(false);
    }
  };

  const student = (pending || passwordStep)?.role === "student";

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <Logo />
        {passwordStep ? (
          <>
            <PasswordForm
              testId="first-password-form"
              title={student ? "Choisis ton mot de passe" : "Choisissez votre mot de passe"}
              intro={
                student
                  ? `Ton courriel ${passwordStep.email} est confirmé. Ce mot de passe te servira à revenir.`
                  : `Votre courriel ${passwordStep.email} est confirmé. Ce mot de passe servira à vous reconnecter.`
              }
              submitLabel={student ? "Ouvrir mon compte" : "Activer le compte"}
              onSubmit={submitPassword}
            />
            {passwordStep.devLinks?.length > 0 && (
              <div className="mt-6 rounded-2xl bg-[#FFF8E1] p-4 text-xs text-[#6b5400]" data-testid="dev-links">
                <p className="font-bold">Mode local : aucun courriel envoyé.</p>
                {passwordStep.devLinks.map((item) => (
                  <p key={item.confirmPath} className="mt-1 break-all">
                    {item.name} : <a className="underline" href={item.confirmPath}>{item.confirmPath}</a>
                  </p>
                ))}
              </div>
            )}
          </>
        ) : pending ? (
          <form onSubmit={submitCode} className="mt-8" data-testid="activation-form">
            <h1 className="font-display text-3xl font-bold text-[#122118]">
              {student ? "Entre ton code" : "Entrez votre code"}
            </h1>
            <p className="mt-2 text-[#4a5f53] leading-relaxed">
              Le code est dans le texto reçu
              {pending.phoneHint ? ` au numéro se terminant par ${pending.phoneHint}.` : "."}
            </p>
            <input
              data-testid="activation-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={5}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
              aria-label="Code d'activation"
              className="mt-6 w-full rounded-2xl border border-gray-200 px-4 py-3 text-center font-display text-2xl tracking-[0.5em] outline-none focus:border-[#25D366]"
            />
            {codeError && <p className="mt-3 text-sm text-[#9b3b3b]">{codeError}</p>}
            <button
              type="submit"
              data-testid="activation-submit"
              disabled={busy || code.length !== 5}
              className="mt-5 w-full rounded-full px-6 py-3.5 text-base font-semibold disabled:opacity-50"
              style={{ background: "var(--sp-primary)", color: "var(--sp-text-on-primary)" }}
            >
              {student ? "Ouvrir mon compte" : "Activer le compte"}
            </button>
          </form>
        ) : (
          <div className="mt-8" data-testid="confirmation-loading">
            {message ? (
              <>
                <h1 className="font-display text-3xl font-bold text-[#122118]">Confirmation impossible</h1>
                <p className="mt-3 text-[#4a5f53]">{message}</p>
              </>
            ) : (
              <span className="block h-6 w-6 animate-spin rounded-full border-2 border-[#25D366] border-t-transparent" aria-label="Chargement" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailConfirmation;
